import { Link } from 'react-router-dom'

import { ATTRIBUTION_MODEL_LABELS } from '@/api/firstPartyTraffic'
import type { ChannelPerformance } from '@/api/types'
import { ChannelBarChart, channelBarRowsFromReport } from '@/components/ChannelBarChart'
import { CoverageIndicator } from '@/components/CoverageIndicator'
import { hasDataQualityContent } from '@/components/dataQuality'
import { DataQualityNotice } from '@/components/DataQualityNotice'
import {
  Badge,
  Card,
  DataTable,
  EmptyState,
  formatCurrency,
  formatInteger,
  formatRatio,
  Icon,
  LoadingBlock,
  type Column,
  type FormatOptions,
} from '@/design'
import { DEFAULT_ATTRIBUTION_MODEL, firstPartyChannelQuery, useChartMetric } from '@/features/analytics/traffic/firstPartyQuery'

import { CardQueryBody } from './CardQueryBody'
import { CHANNEL_CHART_QUERY, useDrillDown, useMoney, useOverviewQuery } from './hooks'

/** A figure nothing reports (spend for an offline channel, and the CPA and ROAS it would give): a dash, never a 0. */
const NOT_REPORTED = <span className="ov-muted">—</span>

function columns(money: FormatOptions): Column<ChannelPerformance>[] {
  return [
    {
      key: 'channel',
      header: 'Channel',
      sortValue: (row) => row.entity.label,
      cell: (row) => (
        <span className="ov-cell-inline">
          {row.entity.label}
          {/* A deleted campaign channel's kind went with it. */}
          {row.kind ? <Badge tone={row.kind === 'offline' ? 'info' : 'neutral'}>{row.kind === 'offline' ? 'Offline' : 'Digital'}</Badge> : null}
        </span>
      ),
    },
    {
      key: 'spend',
      header: 'Spend',
      align: 'end',
      sortValue: (row) => row.spend ?? Number.NEGATIVE_INFINITY,
      cell: (row) => (row.spend === null ? NOT_REPORTED : formatCurrency(row.spend, money)),
    },
    {
      key: 'attributed',
      header: 'Attributed revenue',
      align: 'end',
      sortValue: (row) => row.attributedRevenue,
      cell: (row) => formatCurrency(row.attributedRevenue, money),
    },
    { key: 'orders', header: 'Orders', align: 'end', sortValue: (row) => row.orders, cell: (row) => formatInteger(row.orders) },
    {
      key: 'cpa',
      header: 'CPA',
      align: 'end',
      sortValue: (row) => row.cpa ?? Number.POSITIVE_INFINITY,
      cell: (row) => (row.cpa === null ? NOT_REPORTED : formatCurrency(row.cpa, money)),
    },
    {
      key: 'roas',
      header: 'ROAS',
      align: 'end',
      sortValue: (row) => row.roas ?? -1,
      cell: (row) =>
        row.roas === null ? (
          NOT_REPORTED
        ) : (
          <span className="ov-roas" data-tone={row.roas < 1 ? 'negative' : row.roas >= 3 ? 'positive' : 'neutral'}>
            {formatRatio(row.roas)}
          </span>
        ),
    },
  ]
}

/**
 * The Traffic page's channel chart, fed by the same real first-party data at a fixed model — no toggle for it here.
 * The only tracked figures on the Overview, so the tracking baseline notice and the coverage figure sit with it.
 */
function ChannelChart() {
  const [metric, setMetric] = useChartMetric('ov_chart')
  const chart = useOverviewQuery(CHANNEL_CHART_QUERY, (client, filters) =>
    client.firstPartyTraffic(firstPartyChannelQuery(filters, DEFAULT_ATTRIBUTION_MODEL)),
  )
  const report = chart.data
  if (chart.isPending) return <LoadingBlock rows={4} />
  if (chart.isError || !report) {
    return <EmptyState variant="outage" compact description="Channel chart could not be loaded." />
  }
  return (
    <>
      {hasDataQualityContent(report.dataQuality) ? (
        <div className="ov-data-quality">
          <CoverageIndicator dataQuality={report.dataQuality} />
          <DataQualityNotice dataQuality={report.dataQuality} />
        </div>
      ) : null}
      <ChannelBarChart
        rows={channelBarRowsFromReport(report)}
        metric={metric}
        onMetricChange={setMetric}
        currency={report.currency}
        caption={`TmT first-party, ${ATTRIBUTION_MODEL_LABELS[report.model]} model`}
      />
    </>
  )
}

/** The figures over `rows`: spend only where reported, so revenue with no spend against it never inflates the return. */
function totals(rows: readonly ChannelPerformance[]) {
  const sum = (from: readonly ChannelPerformance[], pick: (row: ChannelPerformance) => number) =>
    from.reduce((total, row) => total + pick(row), 0)
  const withSpend = rows.filter((row) => row.spend !== null)
  const spend = withSpend.length > 0 ? sum(withSpend, (row) => row.spend ?? 0) : null
  return {
    spend,
    attributed: sum(rows, (row) => row.attributedRevenue),
    roas: spend ? sum(withSpend, (row) => row.attributedRevenue) / spend : null,
    digital: sum(rows.filter((row) => row.kind === 'digital'), (row) => row.attributedRevenue),
    offline: sum(rows.filter((row) => row.kind === 'offline'), (row) => row.attributedRevenue),
  }
}

/** Spend and return by channel, digital and offline on the same terms. */
export function MarketingPerformanceCard() {
  const channels = useOverviewQuery('channels', (client, filters) => client.channelPerformance(filters))
  const href = useDrillDown()
  const money = useMoney(channels.data?.meta.currency)
  const rows = channels.data?.data ?? []
  const total = totals(rows)

  return (
    <Card
      title="Marketing performance"
      subtitle="Spend and attributed revenue by channel, online and offline"
      busy={channels.isFetching}
      footer={
        <Link className="ov-footer-link" to={href({ path: '/marketing/channels' })}>
          Compare channels <Icon name="arrowRight" size={14} />
        </Link>
      }
    >
      <div className="ov-channel-chart">
        <ChannelChart />
      </div>
      <CardQueryBody query={channels} loadingRows={6} errorDescription="Channel performance could not be loaded.">
        {() => (
          <>
            {rows.length > 0 ? (
              <dl className="ov-mix">
                <div>
                  <dt>Total spend</dt>
                  <dd>{total.spend === null ? '—' : formatCurrency(total.spend, money)}</dd>
                </div>
                <div>
                  <dt>Attributed revenue</dt>
                  <dd>{formatCurrency(total.attributed, money)}</dd>
                </div>
                <div>
                  <dt>Blended ROAS</dt>
                  <dd>{total.roas === null ? '—' : formatRatio(total.roas)}</dd>
                </div>
                <div>
                  <dt>Digital · offline revenue</dt>
                  <dd>
                    {formatCurrency(total.digital, { ...money, compact: true })} ·{' '}
                    {formatCurrency(total.offline, { ...money, compact: true })}
                  </dd>
                </div>
              </dl>
            ) : null}
            <DataTable
              caption="Marketing performance by channel"
              columns={columns(money)}
              rows={rows}
              rowKey={(row) => row.entity.id}
              rowHref={(row) => href(row.drillDown)}
              initialSort={{ key: 'attributed', direction: 'desc' }}
              empty={<EmptyState variant="no-results" compact title="No channel activity in this period" />}
            />
          </>
        )}
      </CardQueryBody>
    </Card>
  )
}
